/** Сохранённый отчёт конструктора по ссылке (АН-05): данные на выбранную дату, выгрузка, правка и удаление */
import type { CustomReport, ListResponse } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { FileSpreadsheet, FileText, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { ResultView } from './ResultView';
import { sourcesQuery } from './useListView';

const btn =
  'inline-flex h-11 items-center gap-1.5 rounded-md border border-line-strong bg-surface px-3 text-[13px] font-medium text-ink hover:underline md:h-8';

export function CustomReportPage() {
  const mayExport = useCan('analytics.report.export');
  const mayEdit = useCan('analytics.report.edit');
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [asOf, setAsOf] = useState('');
  const sources = useQuery(sourcesQuery);
  const rep = useQuery({
    queryKey: ['reports', 'custom', id],
    queryFn: () => api<CustomReport>(`/reports/custom/${id}`),
  });
  const data = useQuery({
    queryKey: ['reports', 'custom', id, 'data', asOf],
    queryFn: () =>
      api<ListResponse>(`/reports/custom/${id}/data`, { query: { asOf: asOf || undefined, pageSize: 200 } }),
    enabled: rep.isSuccess,
  });
  const del = useMutation({
    mutationFn: () => api<void>(`/reports/custom/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Отчёт удалён');
      void qc.invalidateQueries({ queryKey: ['reports', 'custom'] });
      void navigate({ to: '/reports' });
    },
  });
  useCrumbs(rep.data ? [{ label: rep.data.name }] : null, rep.error);
  if (rep.isPending) return <Spinner />;
  if (rep.isError)
    return (
      <Page>
        <PageHeader title="Отчёт" />
        <ErrorBox error={rep.error} />
      </Page>
    );
  const r = rep.data;
  const q = asOf ? `&asOf=${asOf}` : '';
  return (
    <Page>
      <PageHeader
        title={r.name}
        subtitle={
          <>
            {r.description ? <span className="block">{r.description}</span> : null}
            <span className="block">
              {r.mine ? 'Ваш отчёт' : `Автор: ${r.ownerName ?? '—'}`} · обновлён {dateTime(r.updatedAt)}{' '}
              <Badge tone={r.shared ? 'info' : 'neutral'}>{r.shared ? 'общий' : 'личный'}</Badge>
            </span>
          </>
        }
        actions={
          <>
            {mayExport ? (
              <>
                <a className={btn} href={`/api/v1/reports/custom/${id}/export?format=xlsx${q}`}>
                  <FileSpreadsheet className="size-4" /> Excel
                </a>
                <a className={btn} href={`/api/v1/reports/custom/${id}/export?format=pdf${q}`}>
                  <FileText className="size-4" /> PDF
                </a>
              </>
            ) : null}
            {r.mine && mayEdit ? (
              <>
                <Link to="/reports/builder" search={{ id } as never} className={btn}>
                  <Pencil className="size-4" /> Править
                </Link>
                <button
                  type="button"
                  className={btn}
                  onClick={() => window.confirm(`Удалить отчёт «${r.name}»?`) && del.mutate()}
                >
                  <Trash2 className="size-4" /> Удалить
                </button>
              </>
            ) : null}
          </>
        }
      />
      <Panel
        bodyClassName="p-0"
        title="Данные"
        actions={
          <div className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-2">
            На дату
            <Input
              className="h-11 w-40 md:h-8"
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              aria-label="Дата среза"
            />
          </div>
        }
      >
        {data.isPending ? (
          <Spinner />
        ) : data.isError ? (
          <ErrorBox error={data.error} className="m-4" />
        ) : (
          <>
            {data.data.asOfNote ? (
              <p className="border-b border-line px-4 py-2 text-[12px] text-ink-2">{data.data.asOfNote}</p>
            ) : null}
            <ResultView
              list={data.data}
              config={r.config}
              fields={sources.data?.find((x) => x.key === r.source)?.fields}
            />
            {data.data.total > data.data.rows.length && !data.data.groups ? (
              <p className="border-t border-line px-4 py-3 text-[13px] text-ink-2">
                Показаны первые {data.data.rows.length} из {data.data.total}. Остальное — в выгрузке.
              </p>
            ) : null}
          </>
        )}
      </Panel>
      {del.error ? <ErrorBox error={del.error} className="mt-4" /> : null}
    </Page>
  );
}
